import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Мессенджерное поведение ленты сообщений: старые сверху, новые снизу,
 * открываемся на самом свежем, догрузка старых не дёргает вьюпорт (ТЗ 0032).
 *
 * Механизм жил в треде диалога (`CommunicationsPanel`), а лента канала была
 * «вверх ногами» — новые сверху при поле ввода снизу. Вынесен сюда, чтобы обе
 * ленты вели себя одинаково не на словах: две копии разъехались бы на первой
 * же правке.
 *
 * Три тонкости, каждая оплачена измерением в браузере:
 *
 * 1. **Ключ автоскролла — id самого нового сообщения, а не длина списка.**
 *    Догрузка старых страниц тоже увеличивает длину, и на длине вьюпорт
 *    прыгал бы вниз ровно тогда, когда пользователь листает вверх.
 * 2. **Инвариант якоря — расстояние до НИЗА, а не приращение высоты.** Строки
 *    размечены `content-visibility: auto`, поэтому сразу после рендера
 *    `scrollHeight` — ещё оценка, и «прибавить разницу высот» даёт ноль.
 *    Расстояние до низа при дописывании сверху не меняется по определению.
 * 3. **Восстанавливать позицию нужно в layout-эффекте по счётчику строк, а
 *    НЕ в `requestAnimationFrame` после `fetchMore`.** Промис Apollo
 *    резолвится раньше, чем React коммитит новый DOM: замер показал, что
 *    лента выросла на 2190 px, а оба кадра `rAF` отработали ещё по старой
 *    высоте и не сдвинули `scrollTop` вообще.
 */
interface IArgs {
  /** Смена контекста (диалог/канал) — проскроллить в низ заново. */
  resetKey: string;
  /** Id самого нового загруженного сообщения. */
  newestId: string;
  /** Сколько строк отрисовано — по нему ловится момент домержа страницы. */
  count: number;
}

interface IMessengerScroll<T extends HTMLElement> {
  /** Повесить на скроллящийся контейнер ленты. */
  ref: RefObject<T>;
  /** Обернуть догрузку старых сообщений, чтобы вьюпорт остался на месте. */
  keepAnchored: (load: () => Promise<unknown>) => void;
}

export const useMessengerScroll = <T extends HTMLElement = HTMLDivElement>({
  resetKey,
  newestId,
  count,
}: IArgs): IMessengerScroll<T> => {
  const ref = useRef<T>(null);
  /** Расстояние до низа, замороженное на время догрузки старых сообщений. */
  const anchor = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (anchor.current !== null) {
      // Домержилась страница старых сообщений: возвращаем ту же дистанцию до
      // низа. Второй проход в rAF — на случай, если ленивые высоты
      // (`content-visibility`) досчитались уже после коммита.
      const restore = anchor.current;
      el.scrollTop = el.scrollHeight - restore;
      requestAnimationFrame(() => {
        const node = ref.current;
        if (node) node.scrollTop = node.scrollHeight - restore;
        anchor.current = null;
      });
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [resetKey, newestId, count]);

  const keepAnchored = useCallback((load: () => Promise<unknown>) => {
    const el = ref.current;
    anchor.current = el ? el.scrollHeight - el.scrollTop : 0;
    load()
      // ТЗ 0033: якорь снимаем и на УСПЕХЕ, если страница не принесла ни одной
      // новой строки. Иначе ни одна зависимость layout-эффекта не меняется, он
      // не срабатывает, якорь остаётся заполненным — и СЛЕДУЮЩИЙ диалог
      // открывается посередине ленты вместо самого свежего сообщения.
      .then(() => {
        const node = ref.current;
        if (!node || anchor.current === null) return;
        if (node.scrollHeight - node.scrollTop === anchor.current) {
          anchor.current = null;
        }
      })
      .catch(() => {
        anchor.current = null;
      });
  }, []);

  return { ref, keepAnchored };
};
